import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSpinner, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Popconfirm } from "antd";
import useAxios from "../../../../../MainComponents/Hooks/useAxios.jsx";

function VideoTimedQuestionsPanel({ videoId, enabled }) {
  const [refetch, setRefetch] = useState(false);
  const [mode, setMode] = useState("custom"); // custom | bank
  const [minute, setMinute] = useState(0);
  const [second, setSecond] = useState(0);
  const [question, setQuestion] = useState("");
  const [answer1, setAnswer1] = useState("");
  const [answer2, setAnswer2] = useState("");
  const [answer3, setAnswer3] = useState("");
  const [answer4, setAnswer4] = useState("");
  const [correctAnswer, setCorrectAnswer] = useState("answer1");
  const [categoryId, setCategoryId] = useState("");
  const [bankQuestionId, setBankQuestionId] = useState("");
  const [submitState, setSubmitState] = useState({
    flag: "",
    dependency: false,
    data: {},
  });
  const [deleteState, setDeleteState] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  const [listSuccess, , listLoading] = useAxios(
    `${process.env.REACT_APP_ADMIN_VIDEO_TIMED_QUESTIONS_API}${videoId}/timed-questions`,
    "GET",
    videoId ? "GET" : "",
    refetch
  );

  const [categoriesSuccess] = useAxios(
    process.env.REACT_APP_ADMIN_SETS_CATEGORIES_API,
    "GET",
    "GET"
  );

  const [bankQuestionsSuccess, , bankQuestionsLoading] = useAxios(
    categoryId
      ? `${process.env.REACT_APP_CATEGORY_QUESTIONS_API}${categoryId}`
      : "",
    "GET",
    categoryId ? "GET" : "",
    categoryId
  );

  const addApi =
    mode === "bank"
      ? process.env.REACT_APP_ADMIN_VIDEO_TIMED_QUESTION_BANK_API
      : process.env.REACT_APP_ADMIN_VIDEO_TIMED_QUESTION_CUSTOM_API;

  const [addSuccess, addErrors, addLoading] = useAxios(
    addApi,
    "POST",
    submitState.flag,
    submitState.dependency,
    submitState.data,
    true
  );

  const [deleteSuccess, , deleteLoading] = useAxios(
    process.env.REACT_APP_ADMIN_VIDEO_TIMED_QUESTION_DELETE_API,
    "POST",
    deleteState.flag,
    deleteState.dependency,
    deleteState.data,
    true
  );

  useEffect(() => {
    if (addSuccess || deleteSuccess) {
      setRefetch((v) => !v);
      setQuestion("");
      setAnswer1("");
      setAnswer2("");
      setAnswer3("");
      setAnswer4("");
      setBankQuestionId("");
      setMinute(0);
      setSecond(0);
    }
  }, [addSuccess, deleteSuccess]);

  if (!enabled || !videoId) return null;

  const timestampSec = Number(minute) * 60 + Number(second);
  const items = listSuccess?.data || [];
  const bankQuestions =
    bankQuestionsSuccess?.data?.filter((q) => Number(q.is_essay) === 0) ||
    bankQuestionsSuccess?.data ||
    [];

  const handleAdd = (e) => {
    e.preventDefault();
    if (!timestampSec || timestampSec < 1) return;

    if (mode === "bank") {
      if (!bankQuestionId) return;
      setSubmitState({
        flag: "addBank",
        dependency: !submitState.dependency,
        data: {
          video_id: videoId,
          question_id: bankQuestionId,
          timestamp_sec: timestampSec,
        },
      });
      return;
    }

    if (!question || !answer1 || !answer2 || !answer3 || !answer4) return;
    setSubmitState({
      flag: "addCustom",
      dependency: !submitState.dependency,
      data: {
        video_id: videoId,
        timestamp_sec: timestampSec,
        question,
        answer1,
        answer2,
        answer3,
        answer4,
        correct_answer: correctAnswer,
        difficulty: "medium",
        degree: 1,
      },
    });
  };

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  return (
    <div className="admin-quiz-info-form mt-10 w-3/4 xs:w-5/6 rounded-2xl p-8 md:p-6 shadow-lg">
      <h3 className="mb-2 text-lg font-bold text-slate-800 dark:text-slate-100">
        Timed Questions
      </h3>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">
        أضف أسئلة تظهر للطالب عند دقيقة/ثانية معينة، ولا يكمل الفيديو إلا بعد
        الإجابة الصحيحة.
      </p>

      <div className="mb-6 flex gap-3">
        <button
          type="button"
          onClick={() => setMode("custom")}
          className={`rounded-xl border-2 px-3 py-1.5 text-sm font-semibold transition ${
            mode === "custom"
              ? "border-secondary bg-secondary text-white"
              : "border-secondary text-secondary"
          }`}
        >
          كتابة سؤال
        </button>
        <button
          type="button"
          onClick={() => setMode("bank")}
          className={`rounded-xl border-2 px-3 py-1.5 text-sm font-semibold transition ${
            mode === "bank"
              ? "border-secondary bg-secondary text-white"
              : "border-secondary text-secondary"
          }`}
        >
          من بنك الأسئلة
        </button>
      </div>

      <form onSubmit={handleAdd} className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
          <div className="admin-quiz-info-form__field flex flex-col gap-2">
            <label>الدقيقة</label>
            <input
              className="admin-quiz-info-form__control"
              type="number"
              min={0}
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
            />
          </div>
          <div className="admin-quiz-info-form__field flex flex-col gap-2">
            <label>الثانية</label>
            <input
              className="admin-quiz-info-form__control"
              type="number"
              min={0}
              max={59}
              value={second}
              onChange={(e) => setSecond(e.target.value)}
            />
          </div>
        </div>

        {mode === "custom" ? (
          <>
            <div className="admin-quiz-info-form__field flex flex-col gap-2">
              <label>السؤال</label>
              <textarea
                className="admin-quiz-info-form__control admin-quiz-info-form__textarea"
                rows={3}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-1">
              {[
                ["answer1", answer1, setAnswer1, "A"],
                ["answer2", answer2, setAnswer2, "B"],
                ["answer3", answer3, setAnswer3, "C"],
                ["answer4", answer4, setAnswer4, "D"],
              ].map(([key, val, setter, label]) => (
                <div
                  key={key}
                  className="admin-quiz-info-form__field flex flex-col gap-2"
                >
                  <label>إجابة {label}</label>
                  <input
                    className="admin-quiz-info-form__control"
                    value={val}
                    onChange={(e) => setter(e.target.value)}
                  />
                </div>
              ))}
            </div>
            <div className="admin-quiz-info-form__field flex flex-col gap-2">
              <label>الإجابة الصحيحة</label>
              <select
                className="admin-quiz-info-form__control"
                value={correctAnswer}
                onChange={(e) => setCorrectAnswer(e.target.value)}
              >
                <option value="answer1">A</option>
                <option value="answer2">B</option>
                <option value="answer3">C</option>
                <option value="answer4">D</option>
              </select>
            </div>
          </>
        ) : (
          <>
            <div className="admin-quiz-info-form__field flex flex-col gap-2">
              <label>التصنيف</label>
              <select
                className="admin-quiz-info-form__control"
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setBankQuestionId("");
                }}
              >
                <option value="">اختر التصنيف</option>
                {categoriesSuccess?.data?.map((cat) => (
                  <option key={cat.key || cat.id} value={cat.key || cat.id}>
                    {cat.title || cat.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-quiz-info-form__field flex flex-col gap-2">
              <label>السؤال</label>
              <select
                className="admin-quiz-info-form__control"
                value={bankQuestionId}
                onChange={(e) => setBankQuestionId(e.target.value)}
                disabled={!categoryId || bankQuestionsLoading}
              >
                <option value="">اختر السؤال</option>
                {(Array.isArray(bankQuestions) ? bankQuestions : []).map(
                  (q) => (
                    <option key={q.key || q.id} value={q.key || q.id}>
                      {(q.question || "")
                        .replace(/<[^>]+>/g, " ")
                        .slice(0, 80)}
                    </option>
                  )
                )}
              </select>
            </div>
          </>
        )}

        {addErrors?.response?.data?.message && (
          <p className="text-xs text-red-500">
            {addErrors.response.data.message}
          </p>
        )}

        <button
          type="submit"
          disabled={addLoading && submitState.flag}
          className="admin-quiz-info-form__submit w-full"
        >
          {addLoading && submitState.flag ? (
            <FontAwesomeIcon icon={faSpinner} spin />
          ) : (
            <>
              Add Question <FontAwesomeIcon className="ml-2" icon={faPlus} />
            </>
          )}
        </button>
      </form>

      <div className="mt-8">
        <h4 className="mb-3 font-semibold text-slate-800 dark:text-slate-100">
          الأسئلة المضافة ({items.length})
        </h4>
        {listLoading ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : items.length === 0 ? (
          <p className="text-sm text-slate-500">لا توجد أسئلة بعد.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {items.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-[#1e2a4a] dark:bg-[#0f1630]"
              >
                <div>
                  <p className="mb-1 text-sm font-bold text-secondary">
                    عند {formatTime(item.timestamp_sec)}
                  </p>
                  <div
                    className="text-sm text-slate-700 dark:text-slate-200"
                    dangerouslySetInnerHTML={{
                      __html: item.question || "",
                    }}
                  />
                </div>
                <Popconfirm
                  title="حذف السؤال؟"
                  okText="Confirm"
                  cancelText="Cancel"
                  okType="danger"
                  onConfirm={() =>
                    setDeleteState({
                      flag: "delete",
                      dependency: !deleteState.dependency,
                      data: { id: item.id },
                    })
                  }
                >
                  <button
                    type="button"
                    className="rounded-md border border-red-300 p-2 text-red-400"
                    disabled={deleteLoading && deleteState.flag}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoTimedQuestionsPanel;
